/**
 * src/composables/useI18n.js
 * Lightweight, zero-dependency reactive i18n composable for Vue 3
 * Supports Thai (th) and English (en) with automatic localStorage persistence
 */

import { ref, computed } from 'vue'
import thHome from '../locales/th/home.json'
import enHome from '../locales/en/home.json'

const STORAGE_KEY = 'sko_app_lang'

const messages = {
  th: thHome,
  en: enHome
}

// Initial locale: read from localStorage or default to 'th'
const savedLocale = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
const currentLocale = ref(savedLocale === 'en' ? 'en' : 'th')

/**
 * Resolves nested key path like 'hero.title' or 'selectfile.tabs.link'
 */
function resolveKey(obj, path) {
  if (!obj || !path) return null
  const keys = path.split('.')
  let current = obj
  for (const k of keys) {
    if (current && typeof current === 'object' && k in current) {
      current = current[k]
    } else {
      return null
    }
  }
  return current
}

export function useI18n() {
  /**
   * Translate key with optional fallback
   * @param {string} key e.g. 'hero.title'
   * @param {string} [fallback]
   * @returns {string}
   */
  function t(key, fallback = '') {
    const lang = currentLocale.value
    let val = resolveKey(messages[lang], key)
    if (val !== null && val !== undefined) return val

    // Fallback to Thai if currently in another language
    if (lang !== 'th') {
      val = resolveKey(messages.th, key)
      if (val !== null && val !== undefined) return val
    }

    return fallback || key
  }

  function setLocale(lang) {
    if (lang === 'th' || lang === 'en') {
      currentLocale.value = lang
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, lang)
      }
    }
  }

  function toggleLocale() {
    setLocale(currentLocale.value === 'th' ? 'en' : 'th')
  }

  return {
    locale: currentLocale,
    isThai: computed(() => currentLocale.value === 'th'),
    isEnglish: computed(() => currentLocale.value === 'en'),
    t,
    setLocale,
    toggleLocale
  }
}
